import { ChangeDetectorRef, Directive, Injector, Input, OnInit } from '@angular/core';
import { logger } from '@core/helpers/logger';
import {
  AccountService,
  PlatformService,
  ProfileService,
  ScheduleService,
  SchoolsService,
  UserServiceContractsService,
  UserServicesService,
} from '@core/services';
import { WebsocketService } from '@core/websocket';
// TODO разобраться с Sentry : import * as Sentry from '@sentry/angular';
import { Store } from '@ngrx/store';
import { PaymentConfiguration } from '@shared/checkout-wizards/components/payment-widget';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getProfileSettings } from '@store/reducers/data.reducer';
import {
  AccountDetailsType,
  AnyType,
  CSchool,
  FindAccountStrategyEnum,
  IUserAccess,
  SchoolDetailsType,
  SignupUserServiceContractRequest,
  User,
  UserService,
  UserServiceContract,
  UserServiceContractOrder,
  UserServiceContractOrderCheckoutResponse,
} from 'lingo2-models';
import { first } from 'lodash';
import { catchError, of, throwError, zip } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { AbstractCheckoutWizard } from './abstract-checkout-wizard';

type WizardContext = AbstractUserServiceCheckoutWizard;

type WizardViewType =
  | 'teacher'
  | 'service'
  | 'schedule'
  | 'payment'
  | 'cant-signup-service'
  | 'cant-signup-contract'
  | 'own-service'
  | 'error';

@Directive()
export abstract class AbstractUserServiceCheckoutWizard
  extends AbstractCheckoutWizard<WizardContext, WizardViewType>
  implements OnInit
{
  @Input()
  public set teacherId(value: string) {
    this._selectedTeacherId = value;
  }

  @Input()
  public set userServiceId(value: string) {
    this._selectedUserServiceId = value;
  }

  public userServiceContract: UserServiceContract;
  public userServiceContractOrder: UserServiceContractOrder;
  public school: CSchool;
  public selectedTeacher: User;
  public selectedUserService: UserService;
  public userServices: UserService[] = [];
  public teachers: User[] = [];
  public scheduleReloading = false;

  protected _schoolId: string;
  protected _selectedTeacherId: string;
  protected _selectedUserServiceId: string;

  protected _checkoutDetails: UserServiceContractOrderCheckoutResponse;
  protected _userSettings: Partial<IUserAccess>;
  protected _watchedScheduleUserId: string;
  protected signupProcessing = false;
  protected signupProcessingError: string;
  protected watchForContractOrderPaid$$: AnyType;
  protected accountService: AccountService;
  protected profileService: ProfileService;
  protected schoolsService: SchoolsService;
  protected contractsService: UserServiceContractsService;
  protected userServicesService: UserServicesService;
  protected scheduleService: ScheduleService;
  protected websocketService: WebsocketService;
  protected store: Store;

  protected constructor(
    protected readonly injector: Injector,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(injector, cdr, platform);

    this.accountService = injector.get(AccountService);
    this.profileService = injector.get(ProfileService);
    this.accountService = injector.get(AccountService);
    this.schoolsService = injector.get(SchoolsService);
    this.contractsService = injector.get(UserServiceContractsService);
    this.userServicesService = injector.get(UserServicesService);
    this.scheduleService = injector.get(ScheduleService);
    this.websocketService = injector.get(WebsocketService);
    this.store = injector.get(Store);
  }

  /** Настройка данных для проведения платежа */
  public setupPaymentWidget() {
    zip(
      this.contractsService.getOrderCheckoutDetails(this.userServiceContractOrder?.id),
      this.store.select(getProfileSettings),
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([details, settings]) => {
          this._checkoutDetails = details;
          this._userSettings = settings;

          const order_id = this._checkoutDetails.billing_order_id as string;
          const charge_id = this._checkoutDetails.meetings
            .map((item) => item.participant.options?.billing_charge_id)
            .filter((v) => !!v) as string[];

          this.paymentOptions = {
            first_name: this._me.first_name,
            last_name: this._me.last_name,

            email: this._userSettings.email,
            phone: this._userSettings.mobile_phone,

            country: this._me.country?.alpha2Code,

            order_id,
            charge_id,
            meta: {
              // TODO user_service_contract_id: this.userServiceContractOrder?.user_service_contract_id,
              // TODO user_service_id: this.userServiceContractOrder?.id,
              user_service_contract_order_id: this.userServiceContractOrder?.id,
            },
          };

          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.errorMessage = error.message;
          this.activateView('error');
        },
      });
  }

  /** Обработчик на изменение настроек платёжной формы и параметров проведения платежей */
  public onPaymentConfigured(configuration: PaymentConfiguration) {
    super.onPaymentConfigured(configuration);
    const paymentStep = this.steps.get('payment');
    paymentStep.complete = !!this.paymentConfigured;
    this.detectChanges();
  }

  /** Можно ли начать исполнение платежа */
  public canExecutePayment(): boolean {
    return super.canExecutePayment();
  }

  /** Является ли текущий пользователь выбранным преподавателем */
  public get isOwnService(): boolean {
    if (this._selectedTeacherId) {
      return this._me && this._selectedTeacherId && this._me?.id === this._selectedTeacherId;
    } else {
      return this._me && this.selectedUserService?.author_id === this._me?.id;
    }
  }

  /**
   * Загрузить школу по id
   */
  protected loadSchool$(id: string) {
    if (!id || this.school?.id === id) {
      return of(null);
    }

    const details: SchoolDetailsType[] = ['id', 'title', 'cover_id', 'participants' /* 'teachers', 'stats' */];
    return this.schoolsService.getSchoolByIdOrSlug(id, details, 'LOAD_TIME').pipe(
      tap((school) => {
        this.school = school;
        if (!this.school) {
          this.errorMessage = 'school not found';
          this.activateView('error');
        }
      }),
      catchError((err) => {
        this.errorMessage = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
    );
  }

  /**
   * Загрузить список преподавателей
   *
   * @see onLoadTeachersDone
   */
  protected loadTeachers$(id: string[]) {
    if ((id || []).length === 0) {
      return of(null);
    }

    const details: AccountDetailsType[] = [
      'id',
      'first_name',
      'last_name',
      'cover_id',
      'userpic_id',
      'slug',
      'profile',
      'stats',
    ];
    return this.accountService
      .findAccounts({ use: FindAccountStrategyEnum.id, id }, { page: 1, pageSize: id.length }, details)
      .pipe(
        tap((response) => {
          this.teachers = response.results;

          // если в параметрах @Input был указан конкретный преподаватель
          if (this._selectedTeacherId) {
            const teacher = this.teachers.find((t) => t.id === this._selectedTeacherId);
            if (!teacher) {
              this.errorMessage = 'teacher not found';
              this.activateView('error');
              return;
            }
            this._selectedTeacherId = teacher?.id;
            this.selectedTeacher = teacher;
            return;
          }

          // если преподаватель всего один
          if (this.teachers.length === 1) {
            const teacher = first(this.teachers);
            this._selectedTeacherId = teacher?.id;
            this.selectedTeacher = teacher;
          }
        }),
        tap(() => this.onLoadTeachersDone()),
        catchError((err) => {
          this.errorMessage = err.message;
          this.activateView('error');
          logger.error(err);
          // TODO разобраться с Sentry : Sentry.captureException(err);
          return throwError(err);
        }),
      );
  }

  /** Донастройка мастера после загрузки преподавателей */
  protected onLoadTeachersDone() {}

  /**
   * Загрузить контракт по id
   *
   * @see onLoadUserServiceContractDone
   */
  protected loadUserServiceContract$(id: string) {
    return this.contractsService.findOne(id).pipe(
      tap((contract) => {
        this.userServiceContract = contract;
        if (!this.userServiceContract) {
          this.errorMessage = 'contract not found';
          this.activateView('error');
        }
      }),
      tap(() => this.onLoadUserServiceContractDone()),
      catchError((err) => {
        this.errorMessage = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
    );
  }

  protected onLoadUserServiceContractDone() {}

  /**
   * Записаться на контракт
   *
   * @see onSignupContractDone
   */
  protected signupContract$(user_service_contract_id: string) {
    if (!user_service_contract_id || this.signupProcessing) {
      return of(null);
    }
    this.signupProcessing = true;
    this.signupProcessingError = null;
    this.detectChanges();

    const request = new SignupUserServiceContractRequest({
      user_service_contract_id,
      currency_id: this._me.currency_id,
    });
    return this.contractsService.signup(request).pipe(
      tap((response) => {
        this.userServiceContractOrder = response.model;
        this.userServiceContract = response.contract;
        // ? this._user_service_contractor = response.contractor;
        this.setupWatchContractOrderPaid(this.userServiceContractOrder);
        this.setupPaymentWidget();
      }),
      tap(() => this.onSignupContractDone()),
      catchError((err) => {
        this.signupProcessingError = err.message;
        this.activateView('error');
        logger.error(err);
        // TODO разобраться с Sentry : Sentry.captureException(err);
        return throwError(err);
      }),
      finalize(() => {
        this.signupProcessing = false;
        this.detectChanges();
      }),
    );
  }

  /** Донастройка мастера после записи на услугу */
  protected abstract onSignupContractDone();

  /** Настроить слежение за статусом ордера */
  protected setupWatchContractOrderPaid(order: UserServiceContractOrder) {}

  protected get watchMe$() {
    // дополнительная проверка - не давать записываться на свои услуги
    return super.watchMe$.pipe(
      tap(() => {
        this.detectChanges();
      }),
    );
  }
}
